class EmailTrackingPreferences extends HTMLElement {
  connectedCallback () {
    this.endpointUrl = this.dataset.endpointUrl
    this.store = this.dataset.store
    this.successMessage = this.dataset.successMessage || 'Thank you. Your preference has been updated.'
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again or contact us.'
    this.loadingMessage = this.dataset.loadingMessage || 'Updating your preferences...'
    this.validationEmail = this.dataset.validationEmail || 'Please enter a valid email address'
    this.validationAction = this.dataset.validationAction || 'Please choose an option'
    this.successIcon = this.dataset.successIcon || ''
    this.errorIcon = this.dataset.errorIcon || ''
    this.token = ''

    this.formView = this.querySelector('[data-view="form"]')
    this.resultsView = this.querySelector('[data-view="results"]')
    this.emailInput = this.querySelector('input[name="email"]')
    this.actionInputs = this.querySelectorAll('input[name="tracking_action"]')
    this.submitButton = this.querySelector('[data-action="submit"]')

    this.prefillFromQueryString()
    this.setupEventListeners()
  }

  prefillFromQueryString () {
    try {
      const params = new URLSearchParams(window.location.search)
      const email = params.get('user_email') || params.get('email')
      const token = params.get('token')

      if (email && this.emailInput) {
        this.emailInput.value = email
      }

      if (token) {
        this.token = token
      }
    } catch (error) {
      // ignore malformed query string
    }
  }

  setupEventListeners () {
    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.handleSubmit())
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => this.clearFieldError(this.emailInput))
    }

    this.actionInputs.forEach(input => {
      input.addEventListener('change', () => this.clearActionError())
    })
  }

  getSelectedAction () {
    const checked = Array.from(this.actionInputs).find(input => input.checked)
    return checked ? checked.value : ''
  }

  isValidEmail (email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showFieldError (input, message) {
    const container = input.closest('.email-tracking-preferences__field') || this
    const errorDiv = container.querySelector('.email-tracking-preferences__error')
    if (errorDiv) {
      errorDiv.querySelector('span').textContent = message
      errorDiv.classList.remove('hidden')
    }
  }

  clearFieldError (input) {
    const container = input.closest('.email-tracking-preferences__field') || this
    const errorDiv = container.querySelector('.email-tracking-preferences__error')
    if (errorDiv) {
      errorDiv.classList.add('hidden')
    }
  }

  showActionError (message) {
    const errorDiv = this.querySelector('[data-action-error]')
    if (errorDiv) {
      errorDiv.querySelector('span').textContent = message
      errorDiv.classList.remove('hidden')
    }
  }

  clearActionError () {
    const errorDiv = this.querySelector('[data-action-error]')
    if (errorDiv) {
      errorDiv.classList.add('hidden')
    }
  }

  setButtonLoading (loading) {
    if (!this.submitButton) return
    this.submitButton.classList.toggle('email-tracking-preferences__button--loading', loading)
    this.submitButton.disabled = loading
    this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false')
  }

  async handleSubmit () {
    const email = this.emailInput?.value.trim() || ''
    const action = this.getSelectedAction()
    let isValid = true

    if (!email || !this.isValidEmail(email)) {
      this.showFieldError(this.emailInput, this.validationEmail)
      isValid = false
    }

    if (!action) {
      this.showActionError(this.validationAction)
      isValid = false
    } else {
      this.clearActionError()
    }

    if (!isValid) return

    if (!this.endpointUrl) {
      console.error('Email tracking preferences: no endpoint URL configured.')
      this.showView('results')
      this.renderResults('error')
      return
    }

    this.setButtonLoading(true)
    this.showView('results')
    this.renderResults('loading')

    try {
      /* eslint-disable camelcase */
      const payload = {
        email,
        action,
        store: this.store,
      }
      /* eslint-enable camelcase */

      if (this.token) {
        payload.token = this.token
      }

      const response = await fetch(this.endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error('Request failed')
      }

      this.renderResults('success')
    } catch (error) {
      console.error('Email tracking preference update error:', error)
      this.renderResults('error')
    } finally {
      this.setButtonLoading(false)
    }
  }

  showView (view) {
    this.formView?.classList.toggle('email-tracking-preferences__view--active', view === 'form')
    this.resultsView?.classList.toggle('email-tracking-preferences__view--active', view === 'results')
  }

  renderResults (state) {
    const resultsContainer = this.resultsView?.querySelector('.email-tracking-preferences__results')
    if (!resultsContainer) return

    if (state === 'loading') {
      resultsContainer.setAttribute('aria-live', 'polite')
      resultsContainer.innerHTML = `
        <div class="email-tracking-preferences__result flex flex-col items-center justify-center gap-4">
          <div class="email-tracking-preferences__result-icon animate-spin rounded-full h-12 w-12 border-b-2 border-orange" aria-hidden="true"></div>
          <p class="email-tracking-preferences__result-message text-center text-lg">${this.loadingMessage}</p>
        </div>
      `
    } else if (state === 'success') {
      resultsContainer.innerHTML = `
        <div class="email-tracking-preferences__result flex flex-col items-center justify-center gap-4">
          <div class="email-tracking-preferences__result-icon h-12 w-12 text-success">
            ${this.successIcon}
          </div>
          <p class="email-tracking-preferences__result-message text-center text-lg font-semibold">${this.successMessage}</p>
        </div>
      `
    } else if (state === 'error') {
      resultsContainer.innerHTML = `
        <div class="email-tracking-preferences__result flex flex-col items-center justify-center gap-4">
          <div class="email-tracking-preferences__result-icon h-12 w-12 text-danger">
            ${this.errorIcon}
          </div>
          <p class="email-tracking-preferences__result-message text-center text-lg font-semibold">${this.errorMessage}</p>
        </div>
      `
    }
  }
}

if (!customElements.get('email-tracking-preferences')) {
  customElements.define('email-tracking-preferences', EmailTrackingPreferences)
}
